import { useRef } from 'react';

const graphStatusColor = {
  pending: '#c9b9a5',
  active: '#d97a43',
  completed: '#2f8f6b',
  blocked: '#c65a46',
  skipped: '#8d98a6',
};

const badgeToneStyles = {
  neutral: {
    backgroundColor: 'rgba(148, 163, 184, 0.16)',
    color: '#475569',
  },
  info: {
    backgroundColor: 'rgba(59, 130, 246, 0.14)',
    color: '#1d4ed8',
  },
  success: {
    backgroundColor: 'rgba(34, 197, 94, 0.14)',
    color: '#166534',
  },
  warning: {
    backgroundColor: 'rgba(245, 158, 11, 0.16)',
    color: '#b45309',
  },
  danger: {
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    color: '#b91c1c',
  },
};

const nodeKindLabel = {
  stage: 'Stage',
  decision: 'Decision',
  result: 'Result',
  tool: 'Tool',
};

const fallbackTheme = {
  background:
    'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
  canvasAccent: '#d97a43',
  laneTint: 'rgba(217, 122, 67, 0.08)',
};

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function withAlpha(hex, alpha) {
  if (typeof hex !== 'string') {
    return `rgba(15, 23, 42, ${alpha})`;
  }

  const normalized = hex.replace('#', '');

  if (normalized.length !== 6) {
    return `rgba(15, 23, 42, ${alpha})`;
  }

  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getNodeWidth(node) {
  if (isFiniteNumber(node.width)) {
    return node.width;
  }

  if (node.kind === 'tool') {
    return 208;
  }

  if (node.kind === 'result') {
    return 196;
  }

  return 184;
}

function getNodeHeight(node) {
  if (isFiniteNumber(node.height)) {
    return node.height;
  }

  let height = node.kind === 'tool' ? 118 : 102;

  if (node.subtitle) {
    height += 10;
  }

  if (Array.isArray(node.badges) && node.badges.length > 0) {
    height += 16;
  }

  if (Array.isArray(node.metrics) && node.metrics.length > 0) {
    height += Math.ceil(node.metrics.length / 2) * 30;
  }

  return height;
}

function normalizeNode(node) {
  return {
    ...node,
    x: isFiniteNumber(node.x) ? node.x : 0,
    y: isFiniteNumber(node.y) ? node.y : 0,
    width: getNodeWidth(node),
    height: getNodeHeight(node),
    badges: Array.isArray(node.badges) ? node.badges : [],
    metrics: Array.isArray(node.metrics) ? node.metrics : [],
  };
}

function getAnchorPoint(node, side) {
  switch (side) {
    case 'top':
      return { x: node.x + node.width / 2, y: node.y };
    case 'bottom':
      return { x: node.x + node.width / 2, y: node.y + node.height };
    case 'left':
      return { x: node.x, y: node.y + node.height / 2 };
    case 'right':
    default:
      return { x: node.x + node.width, y: node.y + node.height / 2 };
  }
}

function getAnchorSide(from, to) {
  const fromCenterX = from.x + from.width / 2;
  const fromCenterY = from.y + from.height / 2;
  const toCenterX = to.x + to.width / 2;
  const toCenterY = to.y + to.height / 2;
  const deltaX = toCenterX - fromCenterX;
  const deltaY = toCenterY - fromCenterY;

  if (Math.abs(deltaY) > Math.abs(deltaX) * 0.55) {
    return {
      from: deltaY >= 0 ? 'bottom' : 'top',
      to: deltaY >= 0 ? 'top' : 'bottom',
    };
  }

  return {
    from: deltaX >= 0 ? 'right' : 'left',
    to: deltaX >= 0 ? 'left' : 'right',
  };
}

function getControlPoint(point, side, distance) {
  switch (side) {
    case 'top':
      return { x: point.x, y: point.y - distance };
    case 'bottom':
      return { x: point.x, y: point.y + distance };
    case 'left':
      return { x: point.x - distance, y: point.y };
    case 'right':
    default:
      return { x: point.x + distance, y: point.y };
  }
}

function buildEdgeGeometry(from, to) {
  const anchors = getAnchorSide(from, to);
  const start = getAnchorPoint(from, anchors.from);
  const end = getAnchorPoint(to, anchors.to);
  const distance = Math.max(
    36,
    Math.min(96, Math.max(Math.abs(end.x - start.x), Math.abs(end.y - start.y)) / 2)
  );
  const controlA = getControlPoint(start, anchors.from, distance);
  const controlB = getControlPoint(end, anchors.to, distance);

  return {
    path: `M ${start.x} ${start.y} C ${controlA.x} ${controlA.y}, ${controlB.x} ${controlB.y}, ${end.x} ${end.y}`,
    labelX: (start.x + end.x) / 2,
    labelY: (start.y + end.y) / 2 - 10,
  };
}

function getEdgeStroke(edge, theme) {
  if (edge?.status && theme?.nodePalette?.[edge.status]) {
    return theme.nodePalette[edge.status];
  }

  return graphStatusColor[edge?.status] || graphStatusColor.pending;
}

function getNodeStyle(node, theme) {
  const accent =
    node.accent ||
    theme?.nodePalette?.[node.status] ||
    graphStatusColor[node.status] ||
    fallbackTheme.canvasAccent;
  const surface = node.surface || 'outline';
  const emphasis = node.emphasis || 'low';
  const baseBackground =
    surface === 'solid'
      ? `linear-gradient(180deg, ${withAlpha(accent, 0.22)}, ${withAlpha(accent, 0.12)})`
      : surface === 'glass'
        ? `linear-gradient(180deg, rgba(255, 255, 255, 0.9), ${withAlpha(accent, 0.08)})`
        : 'linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(248, 250, 252, 0.94))';

  return {
    '--graph-node-accent': accent,
    '--graph-node-glow':
      emphasis === 'high'
        ? withAlpha(accent, 0.28)
        : emphasis === 'medium'
          ? withAlpha(accent, 0.2)
          : withAlpha(accent, 0.12),
    left: `${node.x}px`,
    top: `${node.y}px`,
    width: `${node.width}px`,
    minHeight: `${node.height}px`,
    background: baseBackground,
  };
}

function buildCanvas(graph, nodes) {
  const laneBottom = (graph.lanes || []).reduce((max, lane) => {
    const laneY = isFiniteNumber(lane.y) ? lane.y : 0;
    const laneHeight = isFiniteNumber(lane.height) ? lane.height : 0;
    return Math.max(max, laneY + laneHeight);
  }, 0);
  const nodeRight = nodes.reduce((max, node) => Math.max(max, node.x + node.width), 0);
  const nodeBottom = nodes.reduce((max, node) => Math.max(max, node.y + node.height), 0);

  return {
    width: Math.max(isFiniteNumber(graph.width) ? graph.width : 0, nodeRight + 40, 720),
    height: Math.max(isFiniteNumber(graph.height) ? graph.height : 0, nodeBottom + 40, laneBottom + 24, 220),
  };
}

function QueueDebugGraphView({ graph }) {
  const shellRef = useRef(null);
  const dragStateRef = useRef({
    active: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  if (!graph?.nodes?.length) {
    return null;
  }

  const theme = graph.theme || fallbackTheme;
  const nodes = graph.nodes.map(normalizeNode);
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const canvas = buildCanvas(graph, nodes);

  function stopDragging(pointerId) {
    const shell = shellRef.current;
    if (!shell) {
      return;
    }

    if (pointerId !== null && pointerId !== undefined) {
      try {
        shell.releasePointerCapture(pointerId);
      } catch (_error) {
        // Ignore capture release failures when the browser already released it.
      }
    }

    dragStateRef.current.active = false;
    dragStateRef.current.pointerId = null;
    shell.dataset.dragging = 'false';
  }

  function handlePointerDown(event) {
    const shell = shellRef.current;

    if (!shell || (event.pointerType === 'mouse' && event.button !== 0)) {
      return;
    }

    dragStateRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: shell.scrollLeft,
      scrollTop: shell.scrollTop,
    };
    shell.dataset.dragging = 'true';

    try {
      shell.setPointerCapture(event.pointerId);
    } catch (_error) {
      // Pointer capture is best-effort.
    }
  }

  function handlePointerMove(event) {
    const shell = shellRef.current;
    const dragState = dragStateRef.current;

    if (!shell || !dragState.active) {
      return;
    }

    shell.scrollLeft = dragState.scrollLeft - (event.clientX - dragState.startX);
    shell.scrollTop = dragState.scrollTop - (event.clientY - dragState.startY);
  }

  return (
    <div
      ref={shellRef}
      className="debug-graph-shell"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={(event) => stopDragging(event.pointerId)}
      onPointerCancel={(event) => stopDragging(event.pointerId)}
      onPointerLeave={(event) => {
        if (event.pointerType !== 'mouse') {
          stopDragging(event.pointerId);
        }
      }}
    >
      <div
        className="debug-graph-canvas"
        style={{
          width: `${canvas.width}px`,
          minHeight: `${canvas.height}px`,
          background: theme.background || fallbackTheme.background,
          '--graph-canvas-accent': theme.canvasAccent || fallbackTheme.canvasAccent,
        }}
      >
        {(graph.lanes || []).map((lane) => (
          <div
            key={lane.id}
            className="debug-graph-lane"
            style={{
              top: `${isFiniteNumber(lane.y) ? lane.y : 0}px`,
              height: `${isFiniteNumber(lane.height) ? lane.height : 0}px`,
              background: lane.tint || theme.laneTint || fallbackTheme.laneTint,
            }}
          >
            <span className="debug-graph-lane-label">{lane.label}</span>
          </div>
        ))}

        <svg
          className="debug-graph-svg"
          viewBox={`0 0 ${canvas.width} ${canvas.height}`}
          width={canvas.width}
          height={canvas.height}
          aria-label="Queue debug graph"
          role="img"
        >
          {graph.edges.map((edge) => {
            const from = byId.get(edge.from);
            const to = byId.get(edge.to);

            if (!from || !to) {
              return null;
            }

            const { path, labelX, labelY } = buildEdgeGeometry(from, to);
            const stroke = getEdgeStroke(edge, theme);

            return (
              <g key={edge.id}>
                <path
                  d={path}
                  className={[
                    'debug-graph-edge-path',
                    edge.style === 'dashed' ? 'debug-graph-edge-dashed' : '',
                    edge.style === 'glow' ? 'debug-graph-edge-glow' : '',
                    edge.animated ? 'debug-graph-edge-animated' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ stroke }}
                />
                {edge.label ? (
                  <text
                    x={labelX}
                    y={labelY}
                    className="debug-graph-edge-label"
                    textAnchor="middle"
                  >
                    {edge.label}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>

        {nodes.map((node) => (
          <article
            key={node.id}
            className={[
              'debug-graph-node',
              `debug-graph-node-${node.kind || 'stage'}`,
              `debug-graph-surface-${node.surface || 'outline'}`,
              `debug-graph-emphasis-${node.emphasis || 'low'}`,
            ].join(' ')}
            style={getNodeStyle(node, theme)}
          >
            <div className="debug-graph-node-kicker-row">
              <span className="debug-graph-node-kicker">
                {nodeKindLabel[node.kind] || node.kind || 'Node'}
              </span>
              <span className="debug-graph-node-status">{node.status}</span>
            </div>
            <h3 className="debug-graph-node-label">{node.label}</h3>
            {node.subtitle ? (
              <p className="debug-graph-node-subtitle">{node.subtitle}</p>
            ) : null}
            {node.badges.length > 0 ? (
              <div className="debug-graph-node-badges">
                {node.badges.map((badge, index) => (
                  <span
                    key={`${node.id}-badge-${badge.label}-${index}`}
                    className="debug-graph-node-badge"
                    style={badgeToneStyles[badge.tone] || badgeToneStyles.neutral}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>
            ) : null}
            {node.metrics.length > 0 ? (
              <dl className="debug-graph-node-metrics">
                {node.metrics.map((metric, index) => (
                  <div key={`${node.id}-metric-${metric.label}-${index}`}>
                    <dt>{metric.label}</dt>
                    <dd>{metric.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </article>
        ))}
      </div>
    </div>
  );
}

export default QueueDebugGraphView;
